import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { useToast } from '@/shared/ui/Toast';
import { communityApi } from '@/modules/community/api/communityApi';
import { communityKeys } from '@/modules/community/queryKeys';
import type { AgentTask, DraftSection } from '../types';
import { StepTitle } from './BettyReviewsReport';
import { DecisionSlot } from './DecisionSlot';
import styles from './JoeTaskPanel.module.css';
import panels from './TaskPanels.module.css';

/** The Community page composes from these reads; a task action changes what they show. */
function useRefreshCommunity(locationId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: communityKeys.picks(locationId) });
    queryClient.invalidateQueries({ queryKey: communityKeys.network(locationId) });
    queryClient.invalidateQueries({ queryKey: communityKeys.partners(locationId) });
    queryClient.invalidateQueries({ queryKey: communityKeys.cadenceAll(locationId) });
    queryClient.invalidateQueries({ queryKey: communityKeys.referralsAll(locationId) });
  };
}

/**
 * Joe's four task types, rendered with the same report and action-draft frames as every other
 * task but laid out for what they are: a paste-ready note with Copy, reconnect tiles with a touch
 * picker, a one-minute count form, and the weekly picks report with a link to the Community page.
 *
 * The panel owns the action bar for the three action drafts because approving them first writes
 * to the Community module (the planned touch, the confirmed counts) and only then approves the
 * task; the engine's approve carries no form data.
 */

export const JOE_SKILLS = new Set(['community_scout', 'touch_draft', 'cadence_keeper', 'referral_checkin']);
export const JOE_SKILLS_WITH_OWN_ACTIONS = new Set(['touch_draft', 'cadence_keeper', 'referral_checkin']);

const TOUCH_LABELS: Record<string, string> = {
  coffee: 'Coffee',
  lunch: 'Lunch',
  lunch_and_learn: 'Lunch-and-learn',
  treats_for_team: 'Pizza for their team',
  handwritten_note: 'Handwritten note',
  postcard: 'Postcard',
  patient_education_drop: 'Patient education handouts',
  intro_ask: 'Ask for an intro',
  host_at_our_office: 'Host them at our office',
  attend_event: 'Attend event',
  classroom_visit: 'Offer a classroom visit',
};
const NEEDS_WORDS = new Set(['handwritten_note', 'postcard', 'lunch', 'lunch_and_learn', 'intro_ask', 'patient_education_drop', 'attend_event']);
const RHYTHMS: Array<[string, string]> = [['once', 'Just once'], ['3m', 'Every 3 months'], ['6m', 'Every 6 months'], ['12m', 'Every year']];

function text(sections: DraftSection[] | undefined, key: string): string {
  const s = sections?.find((x) => x.key === key);
  if (!s) return '';
  return Array.isArray(s.value) ? s.value.join('\n') : s.value;
}
function list(sections: DraftSection[] | undefined, key: string): string[] {
  const s = sections?.find((x) => x.key === key);
  if (!s) return [];
  return Array.isArray(s.value) ? s.value : s.value.split('\n').filter(Boolean);
}
function wordCount(value: string): number {
  return value.trim().split(/\s+/).filter(Boolean).length;
}
function monthLabel(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (!m) return key;
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function JoeTaskPanel({
  task,
  sections,
  locationId,
  version,
  busy,
  onApprove,
  onReject,
  onOpenThread,
}: {
  task: AgentTask;
  sections: DraftSection[] | undefined;
  locationId: string;
  version: number | null;
  busy: boolean;
  onApprove?: (taskId: string) => void;
  onReject?: (taskId: string, reason?: string) => void;
  onOpenThread: () => void;
}) {
  const why = text(sections, 'why');
  const canAct = task.bucket === 'action' && task.state === 'awaiting_approval' && !!onApprove && !!onReject;
  const resolved = task.state === 'done' ? 'Approved.' : task.state === 'cancelled' ? 'Closed.' : null;
  return (
    <div className={styles.panel}>
      {why && task.skillKey !== 'touch_draft' && (
        <div className={styles.rationale}>
          <strong>Why Joe is proposing this:</strong> {why}
        </div>
      )}
      {task.skillKey === 'touch_draft' && <TouchDraft task={task} sections={sections} locationId={locationId} version={version} busy={busy} canAct={canAct} onApprove={onApprove} onReject={onReject} onOpenThread={onOpenThread} />}
      {task.skillKey === 'cadence_keeper' && <Cadence task={task} sections={sections} locationId={locationId} busy={busy} canAct={canAct} onApprove={onApprove} onReject={onReject} onOpenThread={onOpenThread} />}
      {task.skillKey === 'referral_checkin' && <Referral task={task} sections={sections} locationId={locationId} busy={busy} canAct={canAct} onApprove={onApprove} onReject={onReject} />}
      {task.skillKey === 'community_scout' && (
        <div className={styles.note}>
          Act on each pick from the Community page. <Link to="/community">Open Joe's picks</Link>
        </div>
      )}
      {resolved && task.skillKey !== 'community_scout' && <div className={styles.resolved}>{resolved}</div>}
    </div>
  );
}

function TouchDraft({ task, sections, locationId, version, busy, canAct, onApprove, onReject, onOpenThread }: {
  task: AgentTask; sections: DraftSection[] | undefined; locationId: string; version: number | null; busy: boolean; canAct: boolean;
  onApprove?: (taskId: string) => void; onReject?: (taskId: string, reason?: string) => void; onOpenThread: () => void;
}) {
  const touchId = text(sections, 'touchId');
  const refreshCommunity = useRefreshCommunity(locationId);
  // Tell the Community page which task holds the words for this planned touch, so the partner's
  // history stops saying "Joe is drafting it" and links here instead. Idempotent; failures are quiet.
  useEffect(() => {
    if (!touchId || !locationId) return;
    communityApi.linkDraftTask(locationId, touchId, task.id).then(refreshCommunity).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchId, locationId, task.id]);
  const draft = text(sections, 'draft');
  const alt = text(sections, 'altDraft');
  const print = text(sections, 'printSpec');
  const checks = list(sections, 'checks');
  const why = text(sections, 'why');
  const activity = text(sections, 'activity').trim() || 'Note';
  const to = text(sections, 'partner').trim();
  const plannedFor = text(sections, 'plannedFor').trim();
  const [copied, setCopied] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Record<number, boolean>>({});
  const [whyOpen, setWhyOpen] = useState(false);
  const copy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    } catch {
      setCopied(null);
    }
  };
  let step = 0;
  const nextStep = () => (step += 1);
  return (
    <>
      <div className={panels.panelColumns}>
        <div className={panels.panelLayout}>
          <div className={panels.panelMain}>
            <section className={clsx(panels.take, panels.takeJoe)} aria-label="Joe's take">
              <div className={clsx(panels.takeKicker, panels.takeKickerJoe)}>Joe&apos;s take</div>
              <p className={panels.takeText}>
                {to ? (
                  <>
                    Words for your touch with <strong>{to}</strong>: {activity.charAt(0).toLowerCase() + activity.slice(1)}
                  </>
                ) : (
                  activity
                )}
                {plannedFor ? `, planned for ${plannedFor}` : ''}. Joe wrote the words; you send it yourself. Nothing goes out from PracticeNova.
              </p>
            </section>

            <section className={panels.section}>
              <div className={panels.sectionHead}>
                <StepTitle step={nextStep()} title={to ? `The note, as ${to} will read it` : 'The note'} sub={`${wordCount(draft)} words. Copy it into your card, letter or email.`} />
                <Button variant="secondary" size="sm" onClick={() => copy('draft', draft)}>
                  {copied === 'draft' ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <Letter to={to} body={draft} />
            </section>

            {alt && (
              <section className={panels.section}>
                <div className={panels.sectionHead}>
                  <StepTitle step={nextStep()} title="Or, with a different opener" sub={`${wordCount(alt)} words.`} />
                  <Button variant="secondary" size="sm" onClick={() => copy('alt', alt)}>
                    {copied === 'alt' ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <Letter to={to} body={alt} />
              </section>
            )}

            {print && (
              <section className={panels.section}>
                <StepTitle step={nextStep()} title="How to print it" />
                <div className={panels.box}>{print}</div>
              </section>
            )}
          </div>

          <aside className={panels.panelRail} aria-label="Before you send">
            {checks.length > 0 && (
              <section className={panels.section}>
                <StepTitle
                  step={nextStep()}
                  title="Before you send"
                  sub={`${Object.values(ticked).filter(Boolean).length} of ${checks.length} checked. They're for you; Joe doesn't see them.`}
                />
                <div className={panels.card}>
                  {checks.map((item, i) => (
                    <label key={i} className={panels.checkItem}>
                      <input type="checkbox" className={panels.checkbox} checked={!!ticked[i]} onChange={() => setTicked((t) => ({ ...t, [i]: !t[i] }))} />
                      <span>{item}</span>
                    </label>
                  ))}
                </div>
              </section>
            )}

            {why && (
              <section className={panels.fold}>
                <button type="button" className={panels.foldHeader} aria-expanded={whyOpen} onClick={() => setWhyOpen((v) => !v)}>
                  <span className={panels.foldTitle}>
                    Why Joe suggests this <span className={panels.optional}>Optional</span>
                  </span>
                  <ChevronDown size={18} aria-hidden="true" className={clsx(panels.chevron, whyOpen && panels.chevronOpen)} />
                </button>
                {whyOpen && (
                  <div className={clsx(panels.foldBody, panels.markdown)}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{why}</ReactMarkdown>
                  </div>
                )}
              </section>
            )}
          </aside>
        </div>
      </div>
      {version !== null && version > 1 && <div className={styles.note}>v{version}: revised after your note on v{version - 1}. Earlier versions are in the version bar above.</div>}
      {canAct && (
        <DecisionSlot>
          <div className={styles.actions}>
            <Button variant="primary" size="sm" disabled={busy} onClick={() => onApprove?.(task.id)}>
              Approve, I'll send it
            </Button>
            <Button variant="secondary" size="sm" disabled={busy} onClick={onOpenThread}>
              Request changes
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => {
                // The planned touch stops pointing at these words before the task closes.
                const withdraw = touchId ? communityApi.withdrawDraft(locationId, touchId).catch(() => undefined) : Promise.resolve();
                withdraw.then(() => {
                  refreshCommunity();
                  onReject?.(task.id, 'Not sending this one');
                });
              }}
            >
              Reject
            </Button>
            <span className={styles.tiny}>Nothing is sent by PracticeNova. Approving logs it as planned; you send it.</span>
          </div>
        </DecisionSlot>
      )}
    </>
  );
}

/** A note as its reader will see it: who it is to, then the words in plain type. */
function Letter({ to, body }: { to: string; body: string }) {
  return (
    <div className={panels.letter}>
      {to && <div className={panels.letterTo}>To {to}</div>}
      <div className={panels.letterBody}>{body}</div>
    </div>
  );
}

function Cadence({ task, sections, locationId, busy, canAct, onApprove, onReject, onOpenThread }: {
  task: AgentTask; sections: DraftSection[] | undefined; locationId: string; busy: boolean; canAct: boolean;
  onApprove?: (taskId: string) => void; onReject?: (taskId: string, reason?: string) => void; onOpenThread: () => void;
}) {
  const toast = useToast();
  const partnerId = text(sections, 'partnerId').trim();
  const suggested = text(sections, 'suggestedTouch') || 'handwritten_note';
  const [touch, setTouch] = useState(suggested);
  const [rhythm, setRhythm] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [working, setWorking] = useState(false);
  const { data: due } = useQuery({
    queryKey: communityKeys.cadence(locationId, partnerId),
    queryFn: () => communityApi.getCadence(locationId, partnerId),
    enabled: !!locationId && !!partnerId && canAct,
  });
  useEffect(() => setTouch(suggested), [suggested]);
  const refreshCommunity = useRefreshCommunity(locationId);
  const status = text(sections, 'status');
  const partnerName = text(sections, 'partner').trim() || due?.partner.name || '';
  const overdue = /overdue/i.test(status);
  const approve = async () => {
    if (!partnerId) {
      toast.error('This task has no partner id; approve it from the Community page instead.');
      return;
    }
    setWorking(true);
    try {
      const result = await communityApi.approveCadence(locationId, partnerId, { activityType: touch, rhythm: rhythm ?? undefined, draftRequested: NEEDS_WORDS.has(touch) });
      toast.success(result.draftRequested ? 'Approved. Joe is drafting the words; they will appear here for review.' : 'Approved. The touch is planned in your network.');
      refreshCommunity();
      onApprove?.(task.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not plan the touch');
    } finally {
      setWorking(false);
    }
  };
  const reject = async () => {
    setWorking(true);
    try {
      if (partnerId) await communityApi.skipCadence(locationId, partnerId);
      refreshCommunity();
      onReject?.(task.id, 'Skip this cycle');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not skip this cycle');
    } finally {
      setWorking(false);
    }
  };
  return (
    <>
      {partnerName && <div className={styles.kicker}>Reconnect with {partnerName}</div>}
      <div className={styles.tiles}>
        <div className={styles.tile}>
          <div className={styles.tileLabel}>Last touch</div>
          <div className={styles.tileValue}>{text(sections, 'lastTouch') || (due?.partner.lastTouch ? due.partner.lastTouch.label : 'None yet')}</div>
        </div>
        <div className={styles.tile}>
          <div className={styles.tileLabel}>Your rhythm</div>
          <div className={styles.tileValue}>{due?.partner.rhythmLabel || text(sections, 'rhythm') || ''}</div>
        </div>
        <div className={clsx(styles.tile, overdue && styles.tileAlert)}>
          <div className={styles.tileLabel}>Status</div>
          <div className={styles.tileValue}>{status || due?.partner.statusLabel || ''}</div>
        </div>
        {due?.referralDropOff && (
          <div className={clsx(styles.tile, styles.tileAlert)}>
            <div className={styles.tileLabel}>Referrals</div>
            <div className={styles.tileValue}>Sent patients before, none confirmed for 2 months</div>
          </div>
        )}
      </div>
      <div className={styles.note}>
        <strong>Suggested touch:</strong> {TOUCH_LABELS[touch] ?? touch}. Approve and {NEEDS_WORDS.has(touch) ? 'Joe will draft it for your review' : 'it goes into your network as planned'}.{' '}
        {canAct && (
          <button type="button" className={styles.chip} onClick={() => setChanging((v) => !v)}>
            Want something else?
          </button>
        )}
      </div>
      {changing && (
        <>
          <div className={styles.chips}>
            {Object.entries(TOUCH_LABELS).map(([key, label]) => (
              <button key={key} type="button" className={clsx(styles.chip, touch === key && styles.chipActive)} onClick={() => setTouch(key)}>
                {label}
              </button>
            ))}
          </div>
          <div className={styles.chips}>
            {due?.partner.rhythm === 'custom' && (
              <button type="button" className={clsx(styles.chip, (rhythm ?? 'custom') === 'custom' && styles.chipActive)} onClick={() => setRhythm(null)}>
                Keep {due.partner.rhythmLabel.toLowerCase()}
              </button>
            )}
            {RHYTHMS.map(([key, label]) => (
              <button key={key} type="button" className={clsx(styles.chip, (rhythm ?? due?.partner.rhythm) === key && styles.chipActive)} onClick={() => setRhythm(key)}>
                {label}
              </button>
            ))}
          </div>
        </>
      )}
      {canAct && (
        <DecisionSlot>
          <div className={styles.actions}>
            <Button variant="primary" size="sm" disabled={busy || working} loading={working} onClick={approve}>
              {NEEDS_WORDS.has(touch) ? 'Approve, draft the words' : 'Approve, plan it'}
            </Button>
            <Button variant="secondary" size="sm" disabled={busy || working} onClick={onOpenThread}>
              Request changes
            </Button>
            <Button variant="secondary" size="sm" disabled={busy || working} onClick={reject}>
              Reject, skip this cycle
            </Button>
          </div>
        </DecisionSlot>
      )}
    </>
  );
}

function Referral({ task, sections, locationId, busy, canAct, onApprove, onReject }: {
  task: AgentTask; sections: DraftSection[] | undefined; locationId: string; busy: boolean; canAct: boolean;
  onApprove?: (taskId: string) => void; onReject?: (taskId: string, reason?: string) => void;
}) {
  const toast = useToast();
  const month = text(sections, 'month').trim();
  const validMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(month);
  const { data: rows } = useQuery({
    queryKey: communityKeys.referralRows(locationId, month),
    queryFn: () => communityApi.getReferralRows(locationId, month),
    enabled: !!locationId && validMonth,
  });
  const [values, setValues] = useState<Record<string, string>>({});
  const [working, setWorking] = useState(false);
  useEffect(() => {
    if (!rows) return;
    const next: Record<string, string> = {};
    for (const r of rows) next[r.partnerId] = r.confirmedCount === null ? (r.prefillCount === null ? '' : String(r.prefillCount)) : String(r.confirmedCount);
    setValues(next);
  }, [rows]);
  const fallbackRows = useMemo(() => list(sections, 'rows'), [sections]);
  const refreshCommunity = useRefreshCommunity(locationId);
  // Joe asks once after three skipped months; this is the owner's way to answer.
  const askToTurnOff = /^yes/i.test(text(sections, 'askToTurnOff').trim());
  const turnOff = async () => {
    setWorking(true);
    try {
      await communityApi.updateSettings(locationId, { referralCheckinOn: false });
      toast.success('Monthly referral check-ins are off. Turn them back on from the Joe panel on the Community page.');
      refreshCommunity();
      onReject?.(task.id, 'Check-ins turned off');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not turn check-ins off');
    } finally {
      setWorking(false);
    }
  };
  const confirm = async () => {
    setWorking(true);
    try {
      await communityApi.confirmReferrals(locationId, month, (rows ?? []).map((r) => ({ partnerId: r.partnerId, count: values[r.partnerId]?.trim() === '' || values[r.partnerId] === undefined ? null : Number(values[r.partnerId]) })));
      toast.success('Saved. Counts now show on each partner in My Network.');
      refreshCommunity();
      onApprove?.(task.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the counts');
    } finally {
      setWorking(false);
    }
  };
  const skip = async () => {
    setWorking(true);
    try {
      if (validMonth) await communityApi.skipReferralMonth(locationId, month);
      refreshCommunity();
      onReject?.(task.id, 'Skipped this month');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not skip this month');
    } finally {
      setWorking(false);
    }
  };
  return (
    <>
      <div className={styles.rows}>
        <div className={styles.rowHead}>
          <div>Partner</div>
          <div>Joe's prefill</div>
          <div>{validMonth ? monthLabel(month) : 'Count'}</div>
        </div>
        {rows
          ? rows.map((r) => (
              <div key={r.partnerId} className={styles.row}>
                <label htmlFor={`ref-${r.partnerId}`} className={styles.rowName}>
                  {r.name}
                </label>
                <span className={styles.rowHint}>{r.prefillLabel}</span>
                <input
                  id={`ref-${r.partnerId}`}
                  className={styles.countInput}
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  disabled={!canAct || working}
                  value={values[r.partnerId] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [r.partnerId]: e.target.value }))}
                />
              </div>
            ))
          : fallbackRows.map((line, i) => (
              <div key={i} className={styles.row}>
                <span className={styles.rowName}>{line}</span>
                <span className={styles.rowHint}>No prefill</span>
                <span className={styles.rowHint}>-</span>
              </div>
            ))}
      </div>
      <div className={styles.note}>{text(sections, 'note') || 'Counts only. If you run a referred-by report in your PMS, copy the totals here. Leave blank if you do not know; blank is stored as no data, not zero.'}</div>
      {canAct && (
        <DecisionSlot>
          <div className={styles.actions}>
            <Button variant="primary" size="sm" disabled={busy || working || !rows} loading={working} onClick={confirm}>
              Confirm counts
            </Button>
            <Button variant="secondary" size="sm" disabled={busy || working} onClick={skip}>
              Skip this month
            </Button>
            {askToTurnOff && (
              <Button variant="secondary" size="sm" disabled={busy || working} onClick={turnOff}>
                Turn check-ins off
              </Button>
            )}
          </div>
        </DecisionSlot>
      )}
    </>
  );
}
